/**
 * Het paneel achter het potloodje en achter "Nieuwe zoekopdracht": naam, gebieden,
 * ritme en aan/uit. Dezelfde vorm als het importpaneel: inline onder de kop,
 * velden met hun naam erboven, één gevulde knop, een kruisje om te sluiten.
 *
 * Bij bewerken gaat alleen mee wat anders is. De server bouwt de URL opnieuw op
 * uit de gebieden en laat de overige filters staan; de huidige URL staat onderaan
 * als link, zodat je op Funda kunt nakijken wat de ronde straks opent.
 */
import {
  gebiedLabel,
  RITME_KEUZES,
  type Zoekopdracht,
  type ZoekopdrachtBewerking,
} from '@mamimap/shared';
import { X } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { Melding } from '@/components/Melding';
import { useBewerkZoekopdracht, useGebieden, useMaakZoekopdracht } from '@/lib/api/queries';
import { BuurtKiezer } from './BuurtKiezer';
import { gebiedNamen, kiesOpties } from './stand';

type ZoekopdrachtPaneelProps = {
  /** De zoekopdracht die bewerkt wordt, of null voor een nieuwe. */
  bestaand: Zoekopdracht | null;
  /** Alle zoekopdrachten, voor de slugs die al in gebruik zijn. */
  alle: readonly Zoekopdracht[];
  onKlaar: () => void;
};

export function ZoekopdrachtPaneel({ bestaand, alle, onKlaar }: ZoekopdrachtPaneelProps) {
  const id = useId();
  const [naam, zetNaam] = useState(bestaand?.naam ?? '');
  const [gebieden, zetGebieden] = useState<string[]>(bestaand?.gebieden ?? []);
  const [interval, zetInterval] = useState(bestaand?.intervalMinuten ?? 720);
  const [actief, zetActief] = useState(bestaand?.actief ?? true);
  const [aangeraakt, zetAangeraakt] = useState(false);

  const gebiedenLijst = useGebieden();
  const maak = useMaakZoekopdracht();
  const bewerk = useBewerkZoekopdracht();
  const bezig = maak.isPending || bewerk.isPending;
  const fout = maak.error ?? bewerk.error;

  const namen = useMemo(() => gebiedNamen(gebiedenLijst.data), [gebiedenLijst.data]);
  const label = useCallback((slug: string) => gebiedLabel(slug, namen), [namen]);
  const opties = useMemo(
    () =>
      kiesOpties(gebiedenLijst.data, [...alle.flatMap((een) => een.gebieden), ...gebieden], label),
    [gebiedenLijst.data, alle, gebieden, label],
  );

  const naamFout = naam.trim() === '' ? 'Geef de zoekopdracht een naam.' : null;
  const gebiedFout = gebieden.length === 0 ? 'Kies minstens één gebied.' : null;
  /* Het ritme hoort bij de vaste keuzes te zitten; een rij uit de database kan
     een ander getal dragen, en dan staat dat getal er als extra keuze bij. */
  const ritmes = RITME_KEUZES.some((keuze) => keuze.minuten === interval)
    ? RITME_KEUZES
    : [...RITME_KEUZES, { minuten: interval, label: `elke ${interval} min` }].sort(
        (a, b) => a.minuten - b.minuten,
      );

  function bewaar() {
    zetAangeraakt(true);
    if (naamFout || gebiedFout) return;

    if (!bestaand) {
      maak.mutate(
        { naam: naam.trim(), gebieden, intervalMinuten: interval },
        { onSuccess: onKlaar },
      );
      return;
    }

    const wijziging: ZoekopdrachtBewerking = {};
    if (naam.trim() !== bestaand.naam) wijziging.naam = naam.trim();
    if (!gelijk(gebieden, bestaand.gebieden)) wijziging.gebieden = gebieden;
    if (interval !== bestaand.intervalMinuten) wijziging.intervalMinuten = interval;
    if (actief !== bestaand.actief) wijziging.actief = actief;

    if (Object.keys(wijziging).length === 0) {
      onKlaar();
      return;
    }
    bewerk.mutate({ id: bestaand.id, wijziging }, { onSuccess: onKlaar });
  }

  return (
    <div className="paneel flex flex-col gap-3 p-3">
      <div className="flex items-start justify-between gap-4">
        <span className="opschrift">
          {bestaand ? `Zoekopdracht bewerken` : 'Nieuwe zoekopdracht'}
        </span>
        <button
          type="button"
          onClick={onKlaar}
          aria-label="Paneel sluiten"
          className="text-faint hover:text-ink"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(220px,300px)_1fr]">
        <div className="flex flex-col gap-3">
          <div>
            <label className="veldnaam" htmlFor={`${id}-naam`}>
              Naam
            </label>
            <input
              id={`${id}-naam`}
              className="veld"
              value={naam}
              onChange={(event) => zetNaam(event.target.value)}
              aria-invalid={aangeraakt && naamFout ? 'true' : undefined}
              placeholder="De Pijp en Oud-Zuid"
            />
            {aangeraakt && naamFout ? <p className="veldfout">{naamFout}</p> : null}
          </div>

          <div>
            <label className="veldnaam" htmlFor={`${id}-ritme`}>
              Ritme
            </label>
            <select
              id={`${id}-ritme`}
              className="veld"
              value={interval}
              onChange={(event) => zetInterval(Number(event.target.value))}
            >
              {ritmes.map((keuze) => (
                <option key={keuze.minuten} value={keuze.minuten}>
                  {keuze.label}
                </option>
              ))}
            </select>
          </div>

          {bestaand ? (
            <label className="flex items-center gap-2 text-[12.5px] text-ink-soft">
              <input
                type="checkbox"
                checked={actief}
                onChange={(event) => zetActief(event.target.checked)}
                className="h-3.5 w-3.5 accent-accent"
              />
              Wordt afgelopen
            </label>
          ) : null}

          <p className="text-[11.5px] text-muted">
            Alleen gebied en koop gaan in de zoek-URL; prijs en oppervlakte filter je hier in
            MamiMap. Een prijsgrens in de zoekopdracht zet een woning die erboven uit stijgt op
            verdwenen. De zoekopdrachten van het zoekgebied zijn de uitzondering: die dragen de
            zoekfilters uit het paneel hieronder.
          </p>
        </div>

        <div>
          <span className="veldnaam">Gebieden</span>
          <BuurtKiezer opties={opties} gekozen={gebieden} onWijzig={zetGebieden} label={label} />
          {aangeraakt && gebiedFout ? <p className="veldfout">{gebiedFout}</p> : null}
        </div>
      </div>

      {fout ? <Melding fout={fout} /> : null}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="knop" onClick={bewaar} disabled={bezig}>
          {bezig ? 'Bezig' : bestaand ? 'Opslaan' : 'Zoekopdracht maken'}
        </button>
        <button type="button" className="knop-stil" onClick={onKlaar} disabled={bezig}>
          Annuleren
        </button>
        {bestaand ? (
          <a
            href={bestaand.url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto max-w-[50%] truncate text-[11.5px] text-muted underline-offset-2 hover:text-ink hover:underline"
            title={bestaand.url}
          >
            Zoek-URL op Funda
          </a>
        ) : null}
      </div>
    </div>
  );
}

function gelijk(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((slug) => setB.has(slug));
}
